import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format as formatDateFns } from 'date-fns';
import { toast } from '@/shared/feedback/toast';
import { queryKeys } from '@/shared/query';
import { treatmentCatalogAPI } from '@/modules/treatment-catalog/api/treatmentCatalog.api';
import type { TreatmentCatalogItem } from '@/modules/treatment-catalog/types/treatmentCatalog.types';
import type { BookingDoctorOption } from '@/modules/scheduling/api/availability.api';
import {
  bookingOptionsQueryKey,
  placeholderDoctorsForSameClinic,
  useBookingOptions,
  useDebouncedDurationMinutes,
} from '@/modules/scheduling/hooks/useBookingOptions';
import { dentalChairAPI } from '@/modules/clinic/api/dentalChair.api';
import { appointmentTypeAPI } from '../api/appointmentType.api';
import {
  resolveBookingAnchor,
  buildDoctorsListOptionsParams,
} from '../utils/bookingAnchor';
import type { AppointmentFormMode, AppointmentFormValues } from '../utils/appointmentForm';

export type UseAppointmentFormBookingArgs = {
  mode: AppointmentFormMode;
  initialValues: AppointmentFormValues;
  prefillClinicId?: string | null;
  scopeClinicId: string;
  includeInactiveTypeIds?: string[];
  includeInactiveChairIds?: string[];
  excludeAppointmentId?: string;
  values: Pick<
    AppointmentFormValues,
    'clinicId' | 'doctorId' | 'date' | 'duration' | 'type' | 'chairId' | 'startTime'
  >;
};

/**
 * Bootstrap + booking-option queries and derived doctor/chair policy for AppointmentForm.
 * Keeps query keys aligned with AppointmentTypeSelect / ChairSelect / SchedulePanel for RQ dedupe.
 */
export function useAppointmentFormBooking({
  mode,
  initialValues,
  prefillClinicId = null,
  scopeClinicId,
  includeInactiveTypeIds = [],
  includeInactiveChairIds = [],
  excludeAppointmentId,
  values,
}: UseAppointmentFormBookingArgs) {
  const { t } = useTranslation(['appointment', 'common']);
  const queryClient = useQueryClient();
  const [activeChairCount, setActiveChairCount] = useState(0);
  const activeChairCountRef = useRef(0);
  const requireChairModeRef = useRef<'always' | 'never' | 'if_configured'>(
    'if_configured'
  );

  const knownClinicId =
    mode === 'edit'
      ? initialValues.clinicId
      : prefillClinicId || scopeClinicId || '';

  const catalogQuery = useQuery({
    queryKey: queryKeys.treatmentCatalog.list({}),
    queryFn: async (): Promise<TreatmentCatalogItem[]> => {
      const response = await treatmentCatalogAPI.getAll();
      return response.data ?? [];
    },
  });

  const needsInactiveTypes = includeInactiveTypeIds.length > 0;
  const typesBootstrapQuery = useQuery({
    queryKey: queryKeys.appointment.types({ includeInactive: needsInactiveTypes }),
    queryFn: async () => {
      const response = await appointmentTypeAPI.getAll(needsInactiveTypes);
      return response.data ?? [];
    },
  });

  const needsInactiveChairs = includeInactiveChairIds.length > 0;
  const chairsBootstrapQuery = useQuery({
    queryKey: queryKeys.clinic.chairs(knownClinicId, {
      includeInactive: needsInactiveChairs,
    }),
    queryFn: async () => {
      const response = await dentalChairAPI.getAll(
        knownClinicId,
        needsInactiveChairs
      );
      return response.data ?? [];
    },
    enabled: Boolean(knownClinicId),
  });

  const catalogItems = catalogQuery.data ?? [];
  const durationMinutes = values.duration ?? 30;
  const debouncedDurationMinutes = useDebouncedDurationMinutes(durationMinutes);
  const selectedDateKey = values.date
    ? formatDateFns(values.date, 'yyyy-MM-dd')
    : undefined;

  const bookingAnchor = resolveBookingAnchor({
    doctorId: values.doctorId,
    hasDate: Boolean(values.date),
  });

  const doctorsClinicId = values.clinicId || knownClinicId;

  const doctorsQuery = useBookingOptions(
    doctorsClinicId
      ? buildDoctorsListOptionsParams({
          clinicId: doctorsClinicId,
          doctorId: values.doctorId || undefined,
          dateKey: selectedDateKey,
          excludeAppointmentId,
        })
      : null,
    {
      // Date-anchored fetches change the query key. Keep this clinic's roster
      // so isPending stays false and the dialog does not return to skeleton.
      // The clinic-only cache is warmed while the dialog is closed.
      placeholderData: (previousData, previousQuery) =>
        placeholderDoctorsForSameClinic(
          doctorsClinicId,
          previousData,
          previousQuery?.queryKey
        ) ??
        queryClient.getQueryData(bookingOptionsQueryKey({ clinicId: doctorsClinicId })),
    }
  );

  const bookingPolicy = doctorsQuery.data?.policy ?? null;
  const selectedType = (typesBootstrapQuery.data ?? []).find(
    (item) => item.id === values.type
  );
  const typeOverlap = selectedType?.bookingPolicyOverride?.overlap;
  const typeTightensTimes =
    typeOverlap?.dentist === 'block' ||
    typeOverlap?.dentist === 'warn' ||
    typeOverlap?.chair === 'block' ||
    typeOverlap?.chair === 'warn';

  const slotOptionsQuery = useBookingOptions(
    values.clinicId && values.doctorId && selectedDateKey
      ? {
          clinicId: values.clinicId,
          doctorId: values.doctorId,
          chairId:
            bookingPolicy?.overlap?.chair === 'allow'
              ? undefined
              : values.chairId || undefined,
          appointmentTypeId: typeTightensTimes ? values.type || undefined : undefined,
          date: selectedDateKey,
          durationMinutes: debouncedDurationMinutes,
          excludeAppointmentId,
        }
      : null
  );

  const doctors: BookingDoctorOption[] = doctorsQuery.data?.doctors ?? [];
  const bookingChairs =
    slotOptionsQuery.data?.chairs ?? doctorsQuery.data?.chairs;
  const contextDoctor = slotOptionsQuery.data?.doctors?.find(
    (d) => d.id === values.doctorId
  );
  const listDoctor = doctors.find((d) => d.id === values.doctorId);
  const selectedSlot = slotOptionsQuery.data?.times?.find(
    (slot) => slot.label === values.startTime
  );
  const selectedDoctor =
    contextDoctor ??
    (listDoctor
      ? {
          ...listDoctor,
          ...(selectedSlot
            ? {
                status: selectedSlot.status as BookingDoctorOption['status'],
                message: selectedSlot.message,
              }
            : {}),
        }
      : undefined);

  const requireChairMode =
    (bookingPolicy?.requireChair as 'always' | 'never' | 'if_configured' | undefined) ??
    'if_configured';
  const chairRequired =
    requireChairMode === 'always' ||
    (requireChairMode === 'if_configured' && activeChairCount > 0);

  useEffect(() => {
    requireChairModeRef.current = requireChairMode;
  }, [requireChairMode]);

  useEffect(() => {
    if (
      catalogQuery.isError ||
      typesBootstrapQuery.isError ||
      chairsBootstrapQuery.isError
    ) {
      toast.error(t('toasts.loadDataFailed'));
    }
  }, [
    catalogQuery.isError,
    typesBootstrapQuery.isError,
    chairsBootstrapQuery.isError,
    t,
  ]);

  const handleActiveChairCountChange = (count: number): void => {
    activeChairCountRef.current = count;
    setActiveChairCount(count);
  };

  const resetActiveChairCount = (): void => {
    activeChairCountRef.current = 0;
    setActiveChairCount(0);
  };

  const isBootstrapPending =
    catalogQuery.isPending ||
    typesBootstrapQuery.isPending ||
    (Boolean(knownClinicId) && chairsBootstrapQuery.isPending);

  // Skeleton only when this clinic has no roster yet. A later date change
  // keeps placeholder data, so isPending stays false.
  const isDoctorsPending = Boolean(doctorsClinicId) && doctorsQuery.isPending;
  // Placeholder roster is the previous date — don't offer it or clear a doctor from it.
  const isDoctorsListUnready =
    isDoctorsPending || (Boolean(doctorsClinicId) && doctorsQuery.isPlaceholderData);

  return {
    catalogQuery,
    catalogItems,
    doctorsQuery,
    doctors,
    bookingAnchor,
    bookingPolicy,
    typeTightensTimes,
    bookingChairs,
    selectedDoctor,
    requireChairMode,
    requireChairModeRef,
    chairRequired,
    activeChairCount,
    activeChairCountRef,
    handleActiveChairCountChange,
    resetActiveChairCount,
    debouncedDurationMinutes,
    knownClinicId,
    isBootstrapPending,
    isDoctorsPending,
    isDoctorsListUnready,
  };
}
